import React, { useEffect, useMemo, useState } from "react";
import { api, fmt, stateBadgeStyleObj } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Plus, Trash2, Search, ClipboardList, Banknote, Hourglass, Package } from "lucide-react";

const PAGE_SIZE = 15;

const AdminPanelPurchaseOrdersPage = () => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [page, setPage] = useState(1);

  const [view, setView] = useState(null);
  const [viewLines, setViewLines] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => { setPage(1); }, [tab, search, vendorFilter, stateFilter]);

  const load = async () => {
    try {
      const rows = await api.purchase.list({ limit: 500 });
      setData(rows || []);
    } catch { setData([]); }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        load(),
        api.partners.list({ supplier_rank: true }).catch(() => []),
        api.inventory.warehouses().catch(() => []),
      ]).then(([, p, w]) => {
        setPartners(p || []);
        setWarehouses(w || []);
      });
      setLoaded(true);
    })();
  }, []);

  const openView = async (row) => {
    setView(row);
    setViewLoading(true);
    setViewLines([]);
    try {
      const [detail, lines] = await Promise.all([
        api.purchase.get(row.id),
        api.purchase.lines(row.id),
      ]);
      setView(detail || row);
      setViewLines(lines || []);
    } catch {
      setViewLines([]);
    }
    setViewLoading(false);
  };

  const doAction = async (id, kind, label) => {
    try {
      if (kind === "confirm") await api.purchase.confirm(id);
      if (kind === "receive") await api.purchase.receive(id);
      if (kind === "bill") await api.purchase.vendorBill(id);
      if (kind === "cancel") await api.purchase.cancel(id);
      toast(`${label} successful`, "success");
      setView(null);
      await load();
    } catch (e) {
      toast(e.message || `${label} failed`, "error");
    }
  };

  const newFormInit = () => ({
    partner_id: "",
    user_id: "",
    warehouse_id: "",
    date_order: new Date().toISOString().split("T")[0],
    date_planned: "",
    lines: [],
  });
  const [form, setForm] = useState(newFormInit());

  const loadRefs = async () => {
    try {
      const [p, pr, u, w] = await Promise.all([
        api.partners.list({ supplier_rank: true }).catch(() => []),
        api.products.list({ limit: 500 }).catch(() => []),
        api.users.list().catch(() => []),
        api.inventory.warehouses().catch(() => []),
      ]);
      setPartners(p || []);
      setProducts(pr || []);
      setUsers(u || []);
      setWarehouses(w || []);
    } catch {}
  };

  const openNew = async () => {
    setForm(newFormInit());
    setShowNew(true);
    await loadRefs();
  };

  const addLine = () => {
    setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", product_qty: 1, price_unit: "" }] }));
  };

  const setLine = (i, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) }));
  };

  const removeLine = (i) => {
    setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  };

  const pickProduct = (i, productId) => {
    const prod = products.find((p) => String(p.id) === String(productId));
    if (!prod) return;
    setLine(i, { product_id: productId, product_uom: prod.uom_id, price_unit: prod.standard_price || 0, name: prod.name });
  };

  const lineSubtotal = (l) => {
    const qty = Number(l.product_qty) || 0;
    const price = Number(l.price_unit) || 0;
    return qty * price;
  };

  const orderTotal = useMemo(() => form.lines.reduce((s, l) => s + lineSubtotal(l), 0), [form.lines]);

  const handleCreate = async () => {
    if (!form.partner_id) { toast("Vendor is required", "error"); return; }
    if (form.lines.length === 0) { toast("Add at least one product line", "error"); return; }
    const validLines = form.lines.filter((l) => l.product_id && (Number(l.product_qty) || 0) > 0);
    if (validLines.length === 0) { toast("No valid product lines", "error"); return; }
    setCreating(true);
    try {
      const body = {
        partner_id: parseInt(form.partner_id),
        user_id: form.user_id ? parseInt(form.user_id) : undefined,
        warehouse_id: form.warehouse_id ? parseInt(form.warehouse_id) : undefined,
        date_order: form.date_order ? `${form.date_order}T00:00:00Z` : undefined,
        date_planned: form.date_planned ? `${form.date_planned}T00:00:00Z` : undefined,
        state: "draft",
        lines: validLines.map((l) => ({
          product_id: parseInt(l.product_id),
          product_qty: Number(l.product_qty),
          product_uom: l.product_uom ? parseInt(l.product_uom) : undefined,
          price_unit: Number(l.price_unit) || 0,
          name: l.name,
        })),
      };
      const created = await api.purchase.create(body);
      toast(`Purchase Order ${created?.name || ""} created`, "success");
      setShowNew(false);
      await load();
    } catch (e) {
      toast(e.message || "Create failed", "error");
    }
    setCreating(false);
  };

  const vendorName = (row) => row.partner_name || row.partner_id || "—";

  const stats = useMemo(() => {
    const active = data.filter((r) => r.state !== "cancel");
    const spend = active.reduce((s, r) => s + (Number(r.amount_total) || 0), 0);
    const draft = data.filter((r) => r.state === "draft").length;
    const toReceive = data.filter((r) => r.state === "purchase" && r.receipt_status !== "full").length;
    return { count: data.length, spend, draft, toReceive };
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (tab !== "all" && r.state !== tab) return false;
      if (stateFilter !== "all" && r.state !== stateFilter) return false;
      if (vendorFilter !== "all" && String(r.partner_id) !== vendorFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${r.name || ""} ${r.partner_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, tab, search, vendorFilter, stateFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const tabs = [
    { key: "all", label: "All", count: data.length },
    { key: "draft", label: "Draft", count: data.filter((r) => r.state === "draft").length },
    { key: "purchase", label: "Purchase", count: data.filter((r) => r.state === "purchase").length },
    { key: "done", label: "Done", count: data.filter((r) => r.state === "done").length },
    { key: "cancel", label: "Cancelled", count: data.filter((r) => r.state === "cancel").length },
  ];

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Purchase Orders"
        subtitle="Manage vendor orders, confirmations and goods receipts"
        actions={
          <button onClick={openNew} className="ess-btn ess-btn-primary" style={{ padding: "9px 16px" }}>
            <Plus size={16} />
            New Purchase Order
          </button>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Total Orders" value={String(stats.count)} icon={ClipboardList} color="green" trend={`${data.filter((r) => r.state !== "cancel").length} active`} />
        <StatCard label="Spend" value={fmt.moneyShort(stats.spend)} icon={Banknote} color="amber" trend={fmt.money(stats.spend)} />
        <StatCard label="Draft" value={String(stats.draft)} icon={Hourglass} color="slate" trend="Awaiting confirmation" />
        <StatCard label="To Receive" value={String(stats.toReceive)} icon={Package} color="blue" trend="Confirmed orders to receive" />
      </div>

      <div style={{ display: "flex", gap: "24px", marginBottom: "20px", borderBottom: "1px solid #e2e7e2", overflowX: "auto" }}>
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "9px 2px", border: "none", background: "transparent",
            borderBottom: `2px solid ${tab === t.key ? "#225f2b" : "transparent"}`,
            marginBottom: -1, whiteSpace: "nowrap",
            color: tab === t.key ? "#225f2b" : "#64748b", fontSize: "13px",
            fontWeight: tab === t.key ? "600" : "500", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px"
          }}>
            {t.label}
            <span style={{ background: tab === t.key ? "#f0f7f0" : "#f1f5f9", color: tab === t.key ? "#225f2b" : "#64748b", padding: "1px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: "600" }}>{t.count}</span>
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order or vendor..." style={{ ...inputStyle, padding: "9px 12px 9px 36px" }} />
        </div>
        <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)} style={{ ...inputStyle, minWidth: "180px", cursor: "pointer" }}>
          <option value="all">All Vendors</option>
          {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} style={{ ...inputStyle, minWidth: "160px", cursor: "pointer" }}>
          <option value="all">All Status</option>
          {["draft", "purchase", "done", "cancel"].map((s) => (
            <option key={s} value={s}>{s === "cancel" ? "Cancelled" : s === "purchase" ? "Purchase" : s === "done" ? "Done" : "Draft"}</option>
          ))}
        </select>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <EmptyState icon="📭" title="No purchase orders" subtitle="Create your first order with the New Purchase Order button" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Order #</th>
                  <th className="ess-th" style={{ minWidth: "160px" }}>Vendor</th>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Date</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Warehouse</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "130px" }}>Total</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Receipt</th>
                  <th className="ess-th" style={{ minWidth: "100px" }}>Status</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "70px" }}></th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => (
                  <tr key={row.id} className="ess-tr">
                    <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.name || `PO${String(row.id).padStart(5, "0")}`}</td>
                    <td className="ess-td" style={{ fontWeight: "500", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{vendorName(row)}</td>
                    <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(row.date_order)}</td>
                    <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.warehouse_name || "—"}</td>
                    <td className="ess-td" style={{ fontWeight: "600", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.amount_total)}</td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={{ ...stateBadgeStyleObj(row.receipt_status === "full" ? "invoiced" : "draft"), padding: "4px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "600" }}>
                        {row.receipt_status === "full" ? "Received" : row.state === "done" ? "Received" : row.state === "draft" ? "—" : "Not Received"}
                      </span>
                    </td>
                    <td className="ess-td" style={{ whiteSpace: "nowrap" }}>
                      <span style={stateBadgeStyleObj(row.state)}>{row.state === "cancel" ? "Cancelled" : row.state === "purchase" ? "Purchase" : row.state === "done" ? "Done" : "Draft"}</span>
                    </td>
                    <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button onClick={() => openView(row)} style={{ border: "none", background: "transparent", color: "#225f2b", fontSize: "13px", fontWeight: "500", cursor: "pointer", padding: "4px 8px", borderRadius: "4px", transition: "all 0.2s" }} onMouseEnter={(e) => (e.target.style.background = "#f0f7f0")} onMouseLeave={(e) => (e.target.style.background = "transparent")}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafafa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page <= 1 ? "#f1f5f9" : "white", color: page <= 1 ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page <= 1 ? "not-allowed" : "pointer" }}>← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return <button key={p} onClick={() => setPage(p)} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: p === page ? "#225f2b" : "white", color: p === page ? "white" : "#334155", fontSize: "13px", fontWeight: p === page ? "600" : "500", cursor: "pointer" }}>{p}</button>;
            })}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page >= totalPages ? "#f1f5f9" : "white", color: page >= totalPages ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={!!view} onClose={() => setView(null)} title={view ? `Purchase Order ${view.name || ""}` : ""} size="lg">
        {view && (
          <div>
            <div style={{ display: "grid", gap: "16px", marginBottom: "24px" }} className="ess-rg-2">
              <div><label style={labelStyle}>Vendor</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "600" }}>{view.partner_name || "—"}</p></div>
              <div><label style={labelStyle}>Date</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "500" }}>{fmt.dateTime(view.date_order)}</p></div>
              <div><label style={labelStyle}>Order #</label><p style={{ fontSize: "14px", color: "#1e293b", fontWeight: "500" }}>{view.name || `PO${view.id}`}</p></div>
              <div><label style={labelStyle}>Status</label><span style={stateBadgeStyleObj(view.state)}>{view.state === "cancel" ? "Cancelled" : view.state === "purchase" ? "Purchase" : view.state === "done" ? "Done" : "Draft"}</span></div>
              {view.warehouse_name && <div><label style={labelStyle}>Warehouse</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.warehouse_name}</p></div>}
              {view.partner_city && <div><label style={labelStyle}>City</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.partner_city}</p></div>}
              {view.partner_ref && <div><label style={labelStyle}>Vendor Ref</label><p style={{ fontSize: "14px", color: "#475569" }}>{view.partner_ref}</p></div>}
              {view.date_planned && <div><label style={labelStyle}>Expected Date</label><p style={{ fontSize: "14px", color: "#475569" }}>{fmt.date(view.date_planned)}</p></div>}
            </div>

            <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b", marginBottom: "12px" }}>Order Lines</h4>
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden", marginBottom: "20px" }}>
              {viewLines.length === 0 ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#94a3b8", fontSize: "13px" }}>{viewLoading ? "Loading lines..." : "No lines"}</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ textAlign: "left", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Product</th>
                      <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Qty</th>
                      <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Unit Cost</th>
                      <th style={{ textAlign: "right", padding: "10px 14px", fontSize: "11px", fontWeight: "600", color: "#64748b", textTransform: "uppercase" }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewLines.map((l, i) => (
                      <tr key={i} style={{ borderBottom: i < viewLines.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                        <td style={{ padding: "10px 14px", fontSize: "13px", color: "#1e293b", fontWeight: "500" }}>{l.product_name || l.name || "—"}</td>
                        <td style={{ padding: "10px 14px", fontSize: "13px", color: "#475569", textAlign: "right" }}>{fmt.qty(l.product_qty)}</td>
                        <td style={{ padding: "10px 14px", fontSize: "13px", color: "#475569", textAlign: "right" }}>{fmt.money(l.price_unit)}</td>
                        <td style={{ padding: "10px 14px", fontSize: "13px", fontWeight: "600", color: "#1e293b", textAlign: "right" }}>{fmt.money(l.price_subtotal ?? l.price_unit * l.product_qty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-end", marginBottom: "20px" }}>
              <div style={{ fontSize: "13px", color: "#64748b" }}>Untaxed <span style={{ fontWeight: "600", color: "#1e293b", marginLeft: "12px" }}>{fmt.money(view.amount_untaxed ?? view.amount_total)}</span></div>
              <div style={{ fontSize: "13px", color: "#64748b" }}>Tax <span style={{ fontWeight: "600", color: "#1e293b", marginLeft: "12px" }}>{fmt.money(view.amount_tax)}</span></div>
              <div style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>Total <span style={{ marginLeft: "12px" }}>{fmt.money(view.amount_total)}</span></div>
            </div>

            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "20px", display: "flex", gap: "12px", justifyContent: "flex-end", flexWrap: "wrap" }}>
              {view.state === "draft" && (
                <>
                  <button onClick={() => doAction(view.id, "cancel", "Cancel order")} className="ess-btn ess-btn-danger" style={{ padding: "10px 24px" }}>Cancel</button>
                  <button onClick={() => doAction(view.id, "confirm", "Confirm order")} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>Confirm</button>
                </>
              )}
              {view.state === "purchase" && view.receipt_status !== "full" && (
                <>
                  <button onClick={() => doAction(view.id, "cancel", "Cancel order")} className="ess-btn ess-btn-danger" style={{ padding: "10px 24px" }}>Cancel</button>
                  <button onClick={() => doAction(view.id, "receive", "Receive goods")} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>Receive Goods</button>
                </>
              )}
              {view.state === "cancel" && <p style={{ fontSize: "13px", color: "#e11d48", fontWeight: "500", padding: "10px 0" }}>This order has been cancelled.</p>}
              {view.receipt_status === "full" || view.state === "done" ? <p style={{ fontSize: "13px", color: "#0891b2", fontWeight: "500", padding: "10px 0" }}>Goods received — stock updated.</p> : null}
              {(view.receipt_status === "full" || view.state === "done") && view.invoice_status !== "invoiced" && view.invoice_status !== "paid" && (
                <button onClick={() => doAction(view.id, "bill", "Create vendor bill")} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>Create Vendor Bill</button>
              )}
              {(view.invoice_status === "invoiced" || view.invoice_status === "billed" || view.invoice_status === "paid") && <p style={{ fontSize: "13px", color: "#9333ea", fontWeight: "500", padding: "10px 0" }}>Vendor bill created for this order.</p>}
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showNew} onClose={() => setShowNew(false)} title="New Purchase Order" size="lg">
        <div>
          <div style={{ display: "grid", gap: "16px" }} className="ess-rg-2">
            <FormField label="Vendor *">
              <select value={form.partner_id} onChange={(e) => setForm((f) => ({ ...f, partner_id: e.target.value }))} style={inputStyle}>
                <option value="">Select vendor…</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </FormField>
            <FormField label="Buyer">
              <select value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))} style={inputStyle}>
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.employee_name || u.login}</option>)}
              </select>
            </FormField>
            <FormField label="Order Date">
              <input type="date" value={form.date_order} onChange={(e) => setForm((f) => ({ ...f, date_order: e.target.value }))} style={inputStyle} />
            </FormField>
            <FormField label="Expected Date">
              <input type="date" value={form.date_planned} onChange={(e) => setForm((f) => ({ ...f, date_planned: e.target.value }))} style={inputStyle} />
            </FormField>
            <FormField label="Warehouse">
              <select value={form.warehouse_id} onChange={(e) => setForm((f) => ({ ...f, warehouse_id: e.target.value }))} style={inputStyle}>
                <option value="">Select warehouse…</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </FormField>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "8px 0 12px" }}>
            <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>Order Lines</h4>
            <button onClick={addLine} className="ess-btn ess-btn-secondary" style={{ padding: "4px 10px" }}>
              <Plus size={14} /> Add Line
            </button>
          </div>

          {form.lines.length === 0 && (
            <div className="ess-empty" style={{ marginBottom: "16px" }}>No lines yet — click "Add Line" to add a product</div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "20px" }}>
            {form.lines.map((l, i) => (
              <div key={i} style={{ display: "flex", gap: "8px", alignItems: "center", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px", flexWrap: "wrap" }}>
                <select value={l.product_id} onChange={(e) => pickProduct(i, e.target.value)} style={{ ...inputStyle, flex: "2", minWidth: "180px", background: "white" }}>
                  <option value="">Select product…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.default_code ? ` (${p.default_code})` : ""}</option>)}
                </select>
                <input type="number" min="0" value={l.product_qty} onChange={(e) => setLine(i, { product_qty: e.target.value })} placeholder="Qty" style={{ ...inputStyle, flex: "0 0 70px", background: "white" }} />
                <input type="number" min="0" value={l.price_unit} onChange={(e) => setLine(i, { price_unit: e.target.value })} placeholder="Cost" style={{ ...inputStyle, flex: "0 0 100px", background: "white" }} />
                <div style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", flex: "0 0 90px", textAlign: "right" }}>{fmt.money(lineSubtotal(l))}</div>
                <button onClick={() => removeLine(i)} style={{ border: "none", background: "transparent", color: "#e11d48", cursor: "pointer", padding: "6px", flexShrink: 0 }} title="Remove line">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", padding: "14px 16px", background: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
            <span style={{ fontSize: "14px", color: "#475569" }}>Total ({form.lines.length} lines)</span>
            <span style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>{fmt.money(orderTotal)}</span>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e8f0", paddingTop: "20px" }}>
            <button onClick={() => setShowNew(false)} className="ess-btn ess-btn-secondary" style={{ padding: "10px 24px" }}>Cancel</button>
            <button onClick={handleCreate} disabled={creating} className="ess-btn ess-btn-primary" style={{ padding: "10px 24px" }}>
              {creating ? "Creating…" : "Create Order"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelPurchaseOrdersPage;
